import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", phone: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await register(form);
      toast.success(`Welcome, ${u.name.split(" ")[0]}`);
      nav("/");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20">
      <p className="overline text-emerald-800 mb-2">Create account</p>
      <h1 className="font-display text-3xl font-black text-slate-900 tracking-tight">
        Open your MedSupply account.
      </h1>
      <p className="text-slate-500 mt-2 text-sm">
        Faster checkout, order history and business pricing.
      </p>

      <form onSubmit={onSubmit} className="mt-10 space-y-4">
        <Field label="Full name" required>
          <input
            data-testid={AUTH.nameInput}
            required value={form.name} onChange={(e) => set("name", e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Email" required>
          <input
            data-testid={AUTH.emailInput}
            type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Password" required>
          <input
            data-testid={AUTH.passwordInput}
            type="password" required minLength={6}
            value={form.password} onChange={(e) => set("password", e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Company / Facility (optional)">
          <input
            data-testid={AUTH.companyInput}
            value={form.company} onChange={(e) => set("company", e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Phone (optional)">
          <input
            data-testid={AUTH.phoneInput}
            value={form.phone} onChange={(e) => set("phone", e.target.value)}
            className={inputCls}
          />
        </Field>
        {err && (
          <p data-testid={AUTH.errorMsg} className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
            {err}
          </p>
        )}
        <button
          data-testid={AUTH.submitBtn}
          disabled={busy}
          className="w-full py-3 bg-emerald-900 text-white text-sm font-semibold rounded-sm hover:bg-emerald-800 disabled:opacity-60"
        >
          {busy ? "Creating account…" : "Create account"}
        </button>
      </form>

      <p className="text-sm text-slate-500 mt-6 text-center">
        Already have one?{" "}
        <Link to="/login" className="text-emerald-900 font-semibold hover:underline">Sign in</Link>
      </p>
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2.5 text-sm border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-emerald-900/20 focus:border-emerald-900";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}
